import type { UpdateScheduleRevisionRequest } from "@led-signage/shared/management-api";
import type { TransitionMode } from "@led-signage/shared/release-manifest";

/**
 * スケジュール編集フォームの純粋ロジック。
 *
 * 数値帯と入力規則は[スケジュール仕様](../../docs/specifications/scheduling.md)に従う。
 * APIは帯の外の値も受け付けるため、ここでは拒否せず警告として返す。
 */

export type SchedulePurpose =
  | "normal"
  | "weekly"
  | "limited"
  | "temporary"
  | "emergency";

export interface PriorityBand {
  purpose: SchedulePurpose;
  label: string;
  minimum: number;
  maximum: number;
  /** 用途を選んだときに入れる初期値。 */
  suggested: number;
  hint: string;
}

export const PRIORITY_BANDS: readonly PriorityBand[] = [
  {
    purpose: "normal",
    label: "通常",
    minimum: 0,
    maximum: 99,
    suggested: 0,
    hint: "常時の基本編成",
  },
  {
    purpose: "weekly",
    label: "曜日・時間帯",
    minimum: 100,
    maximum: 299,
    suggested: 150,
    hint: "曜日や時間帯で通常を上書きする",
  },
  {
    purpose: "limited",
    label: "期間限定",
    minimum: 300,
    maximum: 599,
    suggested: 400,
    hint: "開始日と終了日のあるキャンペーン",
  },
  {
    purpose: "temporary",
    label: "臨時・即時",
    minimum: 600,
    maximum: 899,
    suggested: 700,
    hint: "短期間の差し替え",
  },
  {
    purpose: "emergency",
    label: "緊急",
    minimum: 900,
    maximum: 1000,
    suggested: 950,
    hint: "緊急配信責任者が扱う",
  },
];

export function bandFor(priority: number): PriorityBand | null {
  return (
    PRIORITY_BANDS.find(
      (band) => priority >= band.minimum && priority <= band.maximum,
    ) ?? null
  );
}

export function bandOf(purpose: SchedulePurpose): PriorityBand {
  const band = PRIORITY_BANDS.find((item) => item.purpose === purpose);
  // PRIORITY_BANDS は SchedulePurpose を網羅している。
  if (band === undefined) throw new Error(`unknown purpose: ${purpose}`);
  return band;
}

/** 月曜をbit 0、日曜をbit 6とする。 */
export const WEEKDAY_LABELS = ["月", "火", "水", "木", "金", "土", "日"] as const;

export const ALL_DAYS = 0b1111111;
export const WEEKDAYS = 0b0011111;
export const WEEKEND = 0b1100000;

export function hasDay(daysMask: number, index: number): boolean {
  return (daysMask & (1 << index)) !== 0;
}

export function toggleDay(daysMask: number, index: number): number {
  return daysMask ^ (1 << index);
}

export function describeDays(daysMask: number): string {
  if (daysMask === ALL_DAYS) return "毎日";
  if (daysMask === WEEKDAYS) return "平日";
  if (daysMask === WEEKEND) return "土日";
  const selected = WEEKDAY_LABELS.filter((_, index) => hasDay(daysMask, index));
  return selected.length === 0 ? "未選択" : selected.join("・");
}

export interface ScheduleFormState {
  purpose: SchedulePurpose;
  playlistRevisionId: string;
  validFrom: string;
  validUntil: string;
  startTime: string;
  endTime: string;
  daysMask: number;
  priority: number;
  transitionMode: TransitionMode;
  maxWaitSeconds: string;
  displayIds: readonly string[];
}

export function emptyForm(today: string): ScheduleFormState {
  const band = bandOf("normal");
  return {
    purpose: "normal",
    playlistRevisionId: "",
    validFrom: today,
    validUntil: "",
    startTime: "09:00",
    endTime: "18:00",
    daysMask: ALL_DAYS,
    priority: band.suggested,
    transitionMode: "finish_current",
    maxWaitSeconds: "",
    displayIds: [],
  };
}

/** 用途を変えると、その帯の初期値を入れ直す。 */
export function applyPurpose(
  form: ScheduleFormState,
  purpose: SchedulePurpose,
): ScheduleFormState {
  return { ...form, purpose, priority: bandOf(purpose).suggested };
}

export interface FormIssue {
  field: keyof ScheduleFormState;
  message: string;
  severity: "error" | "warning";
}

const TIME_INPUT = /^([01]\d|2[0-3]):([0-5]\d)$/u;
const DATE_INPUT = /^\d{4}-\d{2}-\d{2}$/u;

/** `HH:mm` の入力を、APIが要求する `HH:mm:ss` へ整える。 */
export function toApiTime(value: string): string {
  return TIME_INPUT.test(value) ? `${value}:00` : value;
}

/** APIの `HH:mm:ss` を `<input type="time">` 向けへ落とす。 */
export function toInputTime(value: string): string {
  return value.length >= 5 ? value.slice(0, 5) : value;
}

export function inspect(form: ScheduleFormState): FormIssue[] {
  const issues: FormIssue[] = [];

  if (form.playlistRevisionId.length === 0) {
    issues.push({
      field: "playlistRevisionId",
      message: "プレイリストを選んでください。",
      severity: "error",
    });
  }
  if (!DATE_INPUT.test(form.validFrom)) {
    issues.push({
      field: "validFrom",
      message: "開始日を入力してください。",
      severity: "error",
    });
  }
  if (form.validUntil.length > 0 && !DATE_INPUT.test(form.validUntil)) {
    issues.push({
      field: "validUntil",
      message: "終了日の形式が正しくありません。",
      severity: "error",
    });
  }
  if (
    DATE_INPUT.test(form.validFrom) &&
    DATE_INPUT.test(form.validUntil) &&
    form.validUntil < form.validFrom
  ) {
    issues.push({
      field: "validUntil",
      message: "終了日は開始日以降にしてください。",
      severity: "error",
    });
  }
  if (!TIME_INPUT.test(form.startTime) || !TIME_INPUT.test(form.endTime)) {
    issues.push({
      field: "startTime",
      message: "時刻を入力してください。",
      severity: "error",
    });
  } else if (form.startTime === form.endTime) {
    issues.push({
      field: "endTime",
      message: "開始時刻と終了時刻が同じです。",
      severity: "error",
    });
  }
  if (form.daysMask === 0) {
    issues.push({
      field: "daysMask",
      message: "曜日を1つ以上選んでください。",
      severity: "error",
    });
  }
  if (form.displayIds.length === 0) {
    issues.push({
      field: "displayIds",
      message: "対象のLEDを1つ以上選んでください。",
      severity: "error",
    });
  }
  if (
    !Number.isInteger(form.priority) ||
    form.priority < 0 ||
    form.priority > 1000
  ) {
    issues.push({
      field: "priority",
      message: "優先度は0〜1000の整数で指定してください。",
      severity: "error",
    });
  } else {
    const band = bandOf(form.purpose);
    if (form.priority < band.minimum || form.priority > band.maximum) {
      const actual = bandFor(form.priority);
      issues.push({
        field: "priority",
        message:
          `「${band.label}」の帯は${String(band.minimum)}〜${String(band.maximum)}です。` +
          (actual === null
            ? `${String(form.priority)}はどの帯にも属しません。`
            : `${String(form.priority)}は「${actual.label}」の帯です。`) +
          "指定は可能ですが、解決結果が用途と食い違うことがあります。",
        severity: "warning",
      });
    }
  }
  if (form.maxWaitSeconds.length > 0) {
    const parsed = Number(form.maxWaitSeconds);
    if (
      !/^\d+$/u.test(form.maxWaitSeconds) ||
      !Number.isInteger(parsed) ||
      parsed > 86_400
    ) {
      issues.push({
        field: "maxWaitSeconds",
        message: "最大待ち時間は0〜86400秒で指定してください。",
        severity: "error",
      });
    }
  }

  return issues;
}

export function hasBlockingIssue(issues: readonly FormIssue[]): boolean {
  return issues.some((issue) => issue.severity === "error");
}

/**
 * 深夜跨ぎは開始時刻と終了時刻の大小から導く。
 *
 * 仕様では `spansMidnight=false` が `startTime < endTime`、`true` が
 * `startTime > endTime` を要求する。利用者に別途チェックさせると、時刻と
 * 矛盾した組み合わせを作れてしまうため、入力から決める。
 */
export function toRequest(
  form: ScheduleFormState,
): UpdateScheduleRevisionRequest {
  return {
    playlistRevisionId: form.playlistRevisionId,
    validFrom: form.validFrom,
    validUntil: form.validUntil.length === 0 ? null : form.validUntil,
    startTime: toApiTime(form.startTime),
    endTime: toApiTime(form.endTime),
    spansMidnight: form.startTime > form.endTime,
    daysMask: form.daysMask,
    priority: form.priority,
    transitionMode: form.transitionMode,
    maxWaitSeconds:
      form.maxWaitSeconds.length === 0 ? null : Number(form.maxWaitSeconds),
    displayIds: form.displayIds,
  };
}
